import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { PhotosApi } from './api';

/**
 * What the §6 photo screens need from outside React.
 *
 * `canCapture` is `session.run` and `canDelete` is `session.manage` — BP-05
 * drew that line and BP-13 keeps it: removing a client's photograph is a
 * supervisor's decision. The api checks both again (BP-01 R14); a gate here
 * only decides what the browser draws.
 *
 * `share` is injected because the two apps share differently: the web build
 * has `navigator.share` where the browser supports it, and the native build
 * has Capacitor's plugin. Neither belongs in a feature package — BP-11 §12
 * forbids a Capacitor import outside `src/lib/`.
 */
export interface PhotosDeps {
  api: PhotosApi;
  /** `session.run` — photograph a set, and see the ones already taken. */
  canCapture: boolean;
  /** `session.manage` — remove one. */
  canDelete: boolean;
  /**
   * Hand a photograph to the OS share sheet, or `undefined` where there is none.
   *
   * `undefined` rather than a no-op function: the screen then hides the
   * button instead of drawing one that silently does nothing, which is the
   * same call `features/licensing` makes for the clipboard.
   */
  share?: ((url: string, title: string) => Promise<void>) | undefined;
}

const PhotosContext = createContext<PhotosDeps | null>(null);

/** Provide the photo dependencies to a subtree. */
export function PhotosProvider({
  deps,
  children,
}: {
  deps: PhotosDeps;
  children: ReactNode;
}): JSX.Element {
  return <PhotosContext.Provider value={deps}>{children}</PhotosContext.Provider>;
}

/**
 * Read them back.
 *
 * Throws rather than defaulting: a screen rendered outside the provider would
 * otherwise show "you may not do this" to an owner.
 */
export function usePhotosDeps(): PhotosDeps {
  const deps = useContext(PhotosContext);
  if (deps === null) {
    throw new Error('usePhotosDeps must be used inside a <PhotosProvider>');
  }
  return deps;
}
